import { useEffect, useState, useCallback } from "react";
import { api, todayISO } from "@/lib/api";
import { ShiftCard } from "@/components/ShiftCard";
import { SelectField, TextField } from "@/components/Form";
import { WORKER_ROLES, SOFTWARE, COVERAGE_TYPES, RADIUS_OPTIONS } from "@/constants/options";
import { US_STATES } from "@/constants/states";

const blank = {
  role: "",
  date_from: "",
  date_to: "",
  min_pay: "",
  max_distance: "",
  software: "",
  coverage_type: "",
  city: "",
  state: "",
  only_matching: false,
  urgent_only: false,
};

export default function WorkerOpportunities() {
  const [filters, setFilters] = useState(blank);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f) => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
    const { data } = await api.get("/worker/opportunities", { params });
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="worker-opportunities">
      <p className="overline">Opportunities</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Upcoming shifts near you</h1>

      <div className="card-surface mt-8 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          label="Role"
          options={[{ value: "", label: "Any role" }, ...WORKER_ROLES]}
          value={filters.role}
          onChange={(e) => set("role", e.target.value)}
          testId="filter-role"
        />
        <TextField label="From date" type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} testId="filter-date-from" />
        <TextField label="To date" type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} testId="filter-date-to" />
        <TextField label="Min pay ($/hr)" type="number" value={filters.min_pay} onChange={(e) => set("min_pay", e.target.value)} testId="filter-min-pay" />
        <SelectField
          label="Max distance"
          options={[{ value: "", label: "Any distance" }, ...RADIUS_OPTIONS.map((r) => ({ value: r, label: `${r} miles` }))]}
          value={filters.max_distance}
          onChange={(e) => set("max_distance", e.target.value)}
          testId="filter-distance"
        />
        <SelectField
          label="Software"
          options={[{ value: "", label: "Any software" }, ...SOFTWARE]}
          value={filters.software}
          onChange={(e) => set("software", e.target.value)}
          testId="filter-software"
        />
        <SelectField
          label="Coverage type"
          options={[{ value: "", label: "Any type" }, ...COVERAGE_TYPES]}
          value={filters.coverage_type}
          onChange={(e) => set("coverage_type", e.target.value)}
          testId="filter-coverage"
        />
        <SelectField
          label="State"
          options={[{ value: "", label: "Any state" }, ...US_STATES]}
          value={filters.state}
          onChange={(e) => set("state", e.target.value)}
          testId="filter-state"
        />
        <TextField label="Location contains" value={filters.city} onChange={(e) => set("city", e.target.value)} placeholder="Queens" testId="filter-city" />

        <div className="flex flex-wrap items-end gap-3 sm:col-span-2 lg:col-span-4">
          <button
            onClick={() => set("date_from", todayISO()) || set("date_to", todayISO())}
            className="btn-ghost"
            data-testid="filter-same-day"
          >
            Same day
          </button>
          <button
            onClick={() => set("urgent_only", !filters.urgent_only)}
            className={filters.urgent_only ? "btn-accent" : "btn-ghost"}
            data-testid="filter-urgent"
          >
            Urgent only
          </button>
          <button
            onClick={() => set("only_matching", !filters.only_matching)}
            className={filters.only_matching ? "btn-primary" : "btn-ghost"}
            data-testid="filter-strong-matches"
          >
            Strong matches only
          </button>
          <button onClick={() => setFilters(blank)} className="btn-ghost" data-testid="filter-reset">
            Reset filters
          </button>
          <span className="ml-auto text-sm text-slate-500" data-testid="opportunities-count">
            {items.length} shift{items.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="card-surface mt-8 p-8 text-sm text-slate-600" data-testid="opportunities-empty">
          No shifts match these filters yet.
        </p>
      ) : (
        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          {items.map((s) => (
            <ShiftCard key={s.shift_id} shift={s} to={`/worker/shifts/${s.shift_id}`} />
          ))}
        </div>
      )}
    </div>
  );
}
